import * as React from "react";
import Breadcrumbs from "../core/Breadcrumbs";
import ModernCard from "../core/ModernCard";
import { getOrgUsers } from "../../libs/organization";
import {
  addEmployeeToCommissionConfig,
  getCommissionConfigs,
  removeEmployeeFromCommissionConfig,
} from "../../libs/commission_calculator";
import Select from "react-select";
import Tooltip from "react-tooltip";
import produce from "immer";
import usePromise from "../hooks/usePromise";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSet from "../hooks/useSet";
import Loader from "../core/Loader";
import Gravatar from "../core/Gravatar";
import AddSalespersonModal from "./AddSalespersonModal";

interface IProps {
  org: IOrganization;
}

export default function CommissionConfigAssignment({ org }: IProps) {
  const lockedUserIds = useSet();

  const [users, usersPromise] = usePromise(() => getOrgUsers(org.id));

  const { loading: loadingEmployees, reload: reloadEmployees } = usersPromise;

  const salespeople = useMemo(
    () => (users ? users.filter(u => u.roles.includes("sales")) : []),
    [users]
  );

  const [
    configs,
    { loading: loadingConfigs, reload: reloadConfigs },
  ] = usePromise(() =>
    getCommissionConfigs(org.id).then(cfgs => cfgs.filter(c => !c.archived))
  );

  function reload() {
    return Promise.all([reloadEmployees(), reloadConfigs()]);
  }

  const [selectedConfigId, setSelectedConfigId] = useState(null);

  useEffect(() => {
    if (!selectedConfigId && configs && configs[0]) {
      setSelectedConfigId(configs[0].id);
    }
  }, [configs, selectedConfigId]);

  let selectedConfig: ICommissionConfig,
    employeeIdsInConfig: string[] = [];

  if (selectedConfigId) {
    selectedConfig = configs.find(c => c.id === selectedConfigId);
    employeeIdsInConfig = selectedConfig.users.map(u => u.id);
  }

  const getConfigEmployeeCount = useCallback(
    config => {
      return config.users.filter(user =>
        salespeople.find(salesperson => salesperson.id === user.id)
      ).length;
    },
    [salespeople]
  );

  function getEmployeeConfig(employee) {
    if (employee.commissionConfig) {
      return configs.find(c => c.id === employee.commissionConfig.id);
    } else {
      return null;
    }
  }

  async function addEmployeeToConfig(employee) {
    lockedUserIds.add(employee.id);
    await addEmployeeToCommissionConfig(employee.id, selectedConfigId);
    await reload();
    lockedUserIds.delete(employee.id);
  }

  async function removeEmployeeFromConfig(employee) {
    lockedUserIds.add(employee.id);
    await removeEmployeeFromCommissionConfig(employee.id, selectedConfigId);
    await reload();
    lockedUserIds.delete(employee.id);
  }

  return (
    <>
      <Breadcrumbs
        crumbs={[
          {
            label: "Commission Calculator",
            routerLink: true,
            route: "/",
          },
          {
            label: "Configurations",
            routerLink: true,
            route: "/configs",
          },
          {
            label: "Assign Salespeople",
          },
        ]}
      />
      <div className="columns">
        <div className="column">
          <ModernCard title={"Salespeople"} ta="left" m="0px">
            <AddSalespersonModal users={users} usersPromise={usersPromise} />
            <hr />
            {loadingEmployees ? (
              <Loader />
            ) : (
              salespeople
                .filter(salesperson => !salesperson.commissionConfig)
                .map(e => (
                  <EmployeeListItem
                    employee={e}
                    moveDirection={"RIGHT"}
                    moveFunc={() => addEmployeeToConfig(e)}
                    employeeConfig={getEmployeeConfig(e)}
                    selectedConfig={selectedConfig}
                    loading={lockedUserIds.has(e.id)}
                    key={e.id}
                  />
                ))
            )}
          </ModernCard>
        </div>
        <div className="column">
          <ModernCard title={"Configuration"} ta="left" m="0px">
            <Select
              isLoading={loadingConfigs}
              options={
                configs
                  ? configs.map(c => ({
                      label: `${c.name} (${getConfigEmployeeCount(
                        c
                      )} assigned)`,
                      value: c.id,
                    }))
                  : []
              }
              value={selectedConfigId}
              onChange={(opt: ISelectOption) => setSelectedConfigId(opt.value)}
              clearable={false}
            />
            <hr />
            <div>
              {loadingEmployees ? (
                <Loader />
              ) : (
                salespeople
                  .filter(e => employeeIdsInConfig.includes(e.id))
                  .map(e => (
                    <EmployeeListItem
                      employee={e}
                      moveDirection={"LEFT"}
                      moveFunc={() => removeEmployeeFromConfig(e)}
                      employeeConfig={getEmployeeConfig(e)}
                      selectedConfig={selectedConfig}
                      loading={lockedUserIds.has(e.id)}
                      key={e.id}
                    />
                  ))
              )}
            </div>
          </ModernCard>
        </div>
      </div>
    </>
  );
}

function EmployeeListItem({
  employee,
  employeeConfig,
  moveFunc,
  moveDirection,
  selectedConfig,
  loading,
}) {
  return (
    <>
      <div className={"columns"}>
        {moveDirection === "LEFT" && (
          <div className="column is-narrow" data-tip data-for="leftArrow">
            <button
              className="btn btn-default"
              onClick={moveFunc}
              disabled={loading}
            >
              {loading ? (
                <i className={"fa fa-gear fa-spin"} />
              ) : (
                <i className="fa fa-arrow-left" />
              )}
            </button>
            {selectedConfig && (
              <Tooltip id={"leftArrow"}>
                Remove from {selectedConfig.name}
              </Tooltip>
            )}
          </div>
        )}
        <div className="column">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <Gravatar email={employee.email} size={"30"} />
              </div>
              <div className="level-item">
                <p>{employee.name}</p>
              </div>
            </div>
            <div className="level-right" />
          </div>
        </div>
        {moveDirection === "RIGHT" && (
          <div className="column is-narrow">
            <button
              className="btn btn-default"
              onClick={moveFunc}
              data-tip
              data-for="rightArrow"
              disabled={loading}
            >
              {loading ? (
                <i className={"fa fa-gear fa-spin"} />
              ) : (
                <i className="fa fa-arrow-right" />
              )}
            </button>
            {selectedConfig && (
              <Tooltip id={"rightArrow"}>Add to {selectedConfig.name}</Tooltip>
            )}
          </div>
        )}
      </div>
      <hr />
    </>
  );
}



// WEBPACK FOOTER //
// ./app/javascript/components/commission_calculator/CommissionConfigAssignment.tsx